<template>
  <div class="Breadcrumbs">
    <ProgressBar
      :class="stepWidth"
      :steps="breadcrumbs" />
  </div>
</template>
<script>
import { actions, getters } from 'views/utils'
import ProgressBar from 'components/admin/ProgressBar'

const breadcrumbStatuses = {
  PENDING: 'PENDING',
  COMPLETE: 'COMPLETE',
  UNAVAILABLE: 'UNAVAILABLE',
}

// const breadcrumbsOrder = [
//   'CAPACITY',
//   'INFO',
//   'RESERVATION',
//   'ADVANCE',
//   'MAIN',
//   'MAINTENANCE',
// ]
const breadcrumbsOrder = [
  'CAPACITY',
  'INFO',
  'MAIN',
  'ADVANCE',
  'TRANSFER_ACCEPTANCE',
  'MAIN',
  'MAINTENANCE',
]

const getTheme = status => ({
  // PENDING: 'warning',
  // COMPLETE: 'success',
  PENDING: 'info pending',
  COMPLETE: 'info complete',
  UNAVAILABLE: null,
})[status] || null

export default {
  components: { ProgressBar },
  props: {
    creationBreadcrumbKey: String,
    hasSkippedReservation: Boolean,
    hasSkippedAdvance: Boolean,
    reservationId: Number,
    project: Object,
  },
  data() {
    return {
      isReservationPaymentSkipped: false,
      isAdvancePaymentSkipped: false,

      creationBreadcrumbRoutesPreview: {
        CAPACITY: {
          name: 'SelfReservationAddPreview',
        },
        INFO: {
          name: 'ReserveUserPreview',
        },
      },
    }
  },
  computed: {
    isBranch2024() {
      if (!this.project) return false

      return this.project.stepBranch === '2024'
    },
    stepWidth() {
      return `steps-count-${this.breadcrumbs.length}`
    },
    creationBreadcrumbRoutes() {
      return {
        CAPACITY: {
          name: this.authUser ? 'SelfReservationAdd' : 'SelfReservationAddGuest',
        },
        // INFO: {
        //   name: 'ReserveUser',
        // },
      }
    },
    reservation() {
      if (!this.reservationId) return

      return this.query('reservation', this.reservationId)
    },
    reservationPayments() {
      if (!this.reservationId) return []

      return this.query('payment', {
        reservationId: this.reservationId,
      })
    },
    reservationContracts() {
      if (!this.reservationId) return []

      return this.query('contract', {
        reservationId: this.reservationId,
      })
    },
    breadcrumbs2024() {
      return [
        {
          tag: 'CAPACITY',
          getStatus: this.getCreationBreadcrumbStatus,
          name: 'Galia',
          getRoute: this.getCreationBreadcrumbRoute,
        },
        {
          tag: 'INFO',
          getStatus: this.getCreationBreadcrumbStatus,
          name: 'Duomenys',
          getRoute: this.getCreationBreadcrumbRoute,
        },
        {
          tag: 'MAIN',
          getStatus: this.getContractBreadcrumbStatus,
          name: 'Sutartis',
          getRoute: this.getBreadcrumbRoute,
        },
        ...this.isAdvancePaymentSkipped
          ? []
          : [{
            tag: 'ADVANCE',
            getStatus: this.getBreadcrumbStatus,
            name: 'Avansinis mok.',
            getRoute: this.getBreadcrumbRoute,
          }],
        {
          tag: 'TRANSFER_ACCEPTANCE',
          getStatus: this.getContractBreadcrumbStatus,
          name: 'Perdavimo aktas',
          getRoute: this.getBreadcrumbRoute,
        },
        {
          tag: 'MAIN',
          getStatus: this.getBreadcrumbStatus,
          name: 'Mokėjimas',
          getRoute: this.getBreadcrumbRoute,
        },
        {
          tag: 'MAINTENANCE',
          getStatus: this.getBreadcrumbStatus,
          name: 'Priežiūros mok.',
          getRoute: this.getBreadcrumbRoute,
        },
      ]
    },
    breadcrumbsOld() {
      return [
        {
          tag: 'CAPACITY',
          getStatus: this.getCreationBreadcrumbStatus,
          name: 'Galia',
          getRoute: this.getCreationBreadcrumbRoute,
        },
        {
          tag: 'INFO',
          getStatus: this.getCreationBreadcrumbStatus,
          name: 'Duomenys',
          getRoute: this.getCreationBreadcrumbRoute,
        },
        ...this.isReservationPaymentSkipped || !this.isReservationPaymentSupported
          ? []
          : [{
            tag: 'RESERVATION',
            getStatus: this.getBreadcrumbStatus,
            name: 'Rezervacinis mok.',
            getRoute: this.getBreadcrumbRoute,
          }],
        {
          tag: 'ADVANCE',
          getStatus: this.getBreadcrumbStatus,
          name: 'Avansinis mok.',
          getRoute: this.getBreadcrumbRoute,
        },
        {
          tag: 'MAIN',
          getStatus: this.getBreadcrumbStatus,
          name: 'Pirkimo-pardavimo akt.',
          getRoute: this.getBreadcrumbRoute,
        },
        {
          tag: 'MAINTENANCE',
          getStatus: this.getBreadcrumbStatus,
          name: 'Priežiūros mokestis',
          getRoute: this.getBreadcrumbRoute,
        },
      ]
    },
    breadcrumbs() {
      const crumbs = this.isBranch2024 ? this.breadcrumbs2024 : this.breadcrumbsOld

      return crumbs
        .map(breadcrumb => ({
          ...breadcrumb,
          status: breadcrumb.getStatus(breadcrumb.tag),
        }))
        .map(breadcrumb => ({
          ...breadcrumb,
          theme: getTheme(breadcrumb.status),
          route: breadcrumb.getRoute(breadcrumb.status, breadcrumb.tag),
        }))
    },
    isReservationCreated() {
      return !!this.reservationId
    },
    isReservationPaymentSupported() {
      if (!this.project) return false
      return this.doesStepBranchSupportReservation(this.project.stepBranch)
    },
    ...getters('doesStepBranchSupportReservation'),
  },
  methods: {
    getBreadcrumbRoute(status) {
      if (status === breadcrumbStatuses.UNAVAILABLE) return null

      return { name: 'Reservation', params: { id: this.reservation.id } }
    },
    getCreationBreadcrumbRoute(status, breadcrumbKey) {
      if (this.isReservationCreated) {
        return {
          ...this.creationBreadcrumbRoutesPreview[breadcrumbKey],
          params: {
            id: this.reservation.projectId,
            reservationId: this.reservation.id,
          },
        }
      }

      if (status !== breadcrumbStatuses.COMPLETE) return null

      return this.creationBreadcrumbRoutes[breadcrumbKey]
    },
    getCreationBreadcrumbStatus(breadcrumbKey) {
      if (this.isReservationCreated) return breadcrumbStatuses.COMPLETE

      if (this.creationBreadcrumbKey === breadcrumbKey) return breadcrumbStatuses.PENDING

      const currentBreadcrumbStepIndex = breadcrumbsOrder.indexOf(this.creationBreadcrumbKey)
      if (breadcrumbsOrder.indexOf(breadcrumbKey) < currentBreadcrumbStepIndex) return breadcrumbStatuses.COMPLETE

      return breadcrumbStatuses.UNAVAILABLE
    },
    getBreadcrumbStatus(breadcrumbKey) {
      if (this.isPaymentPaid(breadcrumbKey)) return breadcrumbStatuses.COMPLETE

      if (this.isPaymentCreated(breadcrumbKey)) return breadcrumbStatuses.PENDING

      return breadcrumbStatuses.UNAVAILABLE
    },
    getContractBreadcrumbStatus(breadcrumbKey) {
      const contract = this.reservationContracts.find(contract => contract.type === breadcrumbKey)

      if (!contract) return breadcrumbStatuses.UNAVAILABLE

      if (contract.signedAt) return breadcrumbStatuses.COMPLETE

      return breadcrumbStatuses.PENDING
    },
    isPaymentCreated(breadcrumbKey) {
      return this.reservationPayments.some(payment => payment.type === breadcrumbKey)
    },
    isPaymentPaid(breadcrumbKey) {
      const payments = this.reservationPayments.filter(payment => payment.type === breadcrumbKey)

      if (!payments.length) return false

      const paymentsPaid = payments.filter(payment => !!payment.paidAt)

      return payments.length === paymentsPaid.length
    },
    assertAdvancePaymentChoice() {
      if (!this.isReservationCreated) {
        this.isAdvancePaymentSkipped = !!this.hasSkippedAdvance
        return
      }

      this.isAdvancePaymentSkipped = this.reservation.hasSkippedAdvance
    },
    assertReservationPaymentChoice() {
      if (!this.isReservationCreated) {
        this.isReservationPaymentSkipped = !!this.hasSkippedReservation
        return
      }

      this.isReservationPaymentSkipped = this.reservation.hasSkippedReservation ||
        this.isAdvancePaymentSkipped
    },
    ...actions('FETCH_PAYMENT'),
  },
  watch: {
    hasSkippedReservation: {
      immediate: true,
      handler(hasSkippedReservation) {
        if (hasSkippedReservation === this.isReservationPaymentSkipped) return
        this.assertReservationPaymentChoice()
      },
    },
    hasSkippedAdvance: {
      immediate: true,
      handler(hasSkippedAdvance) {
        if (hasSkippedAdvance === this.AdvancePaymentSkipped) return
        this.assertAdvancePaymentChoice()
      },
    },
  },
  async mounted() {
    this.assertReservationPaymentChoice()
  },
}
</script>

<style lang="scss">
.Breadcrumbs {
  margin-bottom: 6rem;
  @include lg {
    margin-bottom: unset;
  }

  .ProgressBar {
    .ProgressBarStep {
      float: left;
      font-size: 0.6rem;
      padding: 0;
      text-align: center;
      width: calc(100% / 6);
      @include md {
        font-size: unset;
      }

      &::before {
        display: block;
      }

      &::after {
        content: ' ';
      }

    }

    &.steps-count-4 {
      .ProgressBarStep {
        width: calc(100% / 4);
      }
    }

    &.steps-count-5 {
      .ProgressBarStep {
        width: calc(100% / 5);
      }
    }

    &.steps-count-6 {
      .ProgressBarStep {
        width: calc(100% / 6);
      }
    }

    &.steps-count-7 {
      .ProgressBarStep {
        width: calc(100% / 7);
      }
    }

    &.steps-count-8 {
      .ProgressBarStep {
        width: calc(100% / 8);
      }
    }

    // &.hasSkippedReservation {
    //   .ProgressBarStep {
    //     width: calc(100% / 5);
    //   }
    // }

    // &.hasSkippedAdvance {
    //   .ProgressBarStep {
    //     width: calc(100% / 5);
    //   }
    // }
  }
}
</style>
